import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Components } from 'react-markdown';
import { ReactNode, Fragment } from 'react';
import { TrendUp, TrendDown, Info } from '@phosphor-icons/react';

interface MilesMarkdownProps {
  content: string;
}

/**
 * Pattern definitions for semantic highlighting
 */
const PATTERNS = {
  // Currency: $1,234.56 or $1234 or $1,234,567.89
  currency: /\$[\d,]+(?:\.\d{2})?/g,

  // Positive percentage with context: +15%, increased by 15%, up 15%, grew 15%
  positivePercent:
    /(?:\+\d+(?:\.\d+)?%|(?:increased?|up|grew|growth|gain(?:ed)?|rose|higher)\s+(?:by\s+)?\d+(?:\.\d+)?%|\d+(?:\.\d+)?%\s+(?:increase|growth|gain|higher))/gi,

  // Negative percentage with context: -15%, decreased by 15%, down 15%, fell 15%
  negativePercent:
    /(?:-\d+(?:\.\d+)?%|(?:decreased?|down|fell|decline[d]?|drop(?:ped)?|loss|lower)\s+(?:by\s+)?\d+(?:\.\d+)?%|\d+(?:\.\d+)?%\s+(?:decrease|decline|drop|loss|lower))/gi,

  // Neutral percentage (standalone): 15%, 7.5%
  neutralPercent: /\d+(?:\.\d+)?%/g,

  // Key financial terms
  keyTerms:
    /\b(Revenue|Expenses?|Balance|Profit|Loss|Income|Net\s+(?:Income|Profit|Loss)|Cash\s*flow|Total|Savings?)\b/gi,

  // Warning phrases
  warnings:
    /\b(warning|caution|alert|attention|important|note|risk|concern|issue|problem|careful)\b:?/gi,

  // Positive phrases
  positives:
    /\b(great|excellent|good\s+news|healthy|strong|positive|success(?:ful)?|improved|on\s+track)\b/gi,
};

/**
 * Context patterns for determining currency color
 */
const CURRENCY_CONTEXT = {
  // Revenue/positive context - always show in green
  revenue:
    /\b(revenue|rpm|revenue[- ]?per[- ]?mile|earnings?|sales|received|deposits?|payments?\s+received)\b/i,
  // Expense/negative context - always show in red
  expense:
    /\b(expenses?|loss|losses|costs?|cpm|cost[- ]?per[- ]?mile|spending|spent|paid|payment|fees?|charges?|deductions?)\b/i,
  // Conditional context - green if positive, red if negative/zero
  // These terms can be positive or negative depending on the value
  conditional:
    /\b(net\s+cash\s*flow|net\s+profit|gross\s+profit|net\s+income|profit|income|cash\s*flow|net\s+earnings|operating\s+income|ebitda)\b/i,
};

/**
 * Checks if a currency value is negative by examining the text around it
 * Handles formats like: -$5,000 | ($5,000) | negative $5,000 | -$5,000.00
 */
function isCurrencyNegative(
  text: string,
  matchIndex: number,
  matchText: string
): boolean {
  // Check for minus sign immediately before the $ (e.g., "-$5,000")
  if (matchIndex > 0) {
    const charBefore = text[matchIndex - 1];
    if (charBefore === '-') {
      return true;
    }
    // Check for opening parenthesis (accounting notation for negative)
    if (charBefore === '(') {
      return true;
    }
  }

  // Check for "negative" or "minus" in the preceding text (within 20 chars)
  const lookbackStart = Math.max(0, matchIndex - 20);
  const nearbyText = text.slice(lookbackStart, matchIndex).toLowerCase();
  if (/\b(negative|minus)\s*$/.test(nearbyText)) {
    return true;
  }

  // Check if the number itself starts with a minus inside (e.g., "$-5,000")
  if (matchText.includes('-')) {
    return true;
  }

  return false;
}

/**
 * Determines the context type for a currency value by looking at preceding text
 * and the value itself (for conditional contexts)
 */
function getCurrencyContext(
  text: string,
  matchIndex: number,
  matchText: string
): 'revenue' | 'expense' | 'neutral' {
  // Look at the 60 characters before the currency match
  const lookbackStart = Math.max(0, matchIndex - 60);
  const precedingText = text.slice(lookbackStart, matchIndex);

  // Check for conditional context FIRST (net profit, gross profit, etc.)
  // These depend on whether the value is positive or negative
  if (CURRENCY_CONTEXT.conditional.test(precedingText)) {
    const isNegative = isCurrencyNegative(text, matchIndex, matchText);
    return isNegative ? 'expense' : 'revenue';
  }

  // Check for expense context (always red)
  if (CURRENCY_CONTEXT.expense.test(precedingText)) {
    return 'expense';
  }

  // Check for revenue context (always green)
  if (CURRENCY_CONTEXT.revenue.test(precedingText)) {
    return 'revenue';
  }

  return 'neutral';
}

/**
 * Processes a text string and returns React nodes with semantic highlighting
 */
function processTextWithHighlighting(text: string): ReactNode[] {
  if (!text || typeof text !== 'string') return [text];

  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  // Collect all matches with their positions and types
  interface Match {
    index: number;
    length: number;
    text: string;
    type:
      | 'currency'
      | 'positivePercent'
      | 'negativePercent'
      | 'neutralPercent'
      | 'keyTerm'
      | 'warning'
      | 'positive';
  }

  const matches: Match[] = [];

  // Find currency matches
  let match;
  const currencyRegex = new RegExp(PATTERNS.currency.source, 'g');
  while ((match = currencyRegex.exec(text)) !== null) {
    matches.push({
      index: match.index,
      length: match[0].length,
      text: match[0],
      type: 'currency',
    });
  }

  // Find positive percentage matches (check these before neutral)
  const posPercentRegex = new RegExp(PATTERNS.positivePercent.source, 'gi');
  while ((match = posPercentRegex.exec(text)) !== null) {
    matches.push({
      index: match.index,
      length: match[0].length,
      text: match[0],
      type: 'positivePercent',
    });
  }

  // Find negative percentage matches
  const negPercentRegex = new RegExp(PATTERNS.negativePercent.source, 'gi');
  while ((match = negPercentRegex.exec(text)) !== null) {
    matches.push({
      index: match.index,
      length: match[0].length,
      text: match[0],
      type: 'negativePercent',
    });
  }

  // Sort matches by position
  matches.sort((a, b) => a.index - b.index);

  // Remove overlapping matches (keep the first one)
  const filteredMatches: Match[] = [];
  let lastEnd = 0;
  for (const m of matches) {
    if (m.index >= lastEnd) {
      filteredMatches.push(m);
      lastEnd = m.index + m.length;
    }
  }

  // Build the result nodes
  for (const m of filteredMatches) {
    // Add text before this match
    if (m.index > lastIndex) {
      nodes.push(text.slice(lastIndex, m.index));
    }

    // Add the styled match
    switch (m.type) {
      case 'currency': {
        // Determine color based on surrounding context and value sign
        const context = getCurrencyContext(text, m.index, m.text);
        const currencyStyles = {
          revenue: 'bg-emerald-50 text-emerald-700',
          expense: 'bg-red-50 text-red-700',
          neutral: 'bg-gray-100 text-gray-700',
        };
        nodes.push(
          <span
            key={key++}
            className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 font-semibold ${currencyStyles[context]}`}
          >
            {m.text}
          </span>
        );
        break;
      }

      case 'positivePercent':
        nodes.push(
          <span
            key={key++}
            className="inline-flex items-center gap-0.5 font-semibold text-emerald-600"
          >
            <TrendUp size={14} weight="bold" className="inline" />
            {m.text}
          </span>
        );
        break;

      case 'negativePercent':
        nodes.push(
          <span
            key={key++}
            className="inline-flex items-center gap-0.5 font-semibold text-red-600"
          >
            <TrendDown size={14} weight="bold" className="inline" />
            {m.text}
          </span>
        );
        break;

      case 'neutralPercent':
        nodes.push(
          <span key={key++} className="font-semibold text-gray-700">
            {m.text}
          </span>
        );
        break;

      default:
        nodes.push(m.text);
    }

    lastIndex = m.index + m.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes.length > 0 ? nodes : [text];
}

/**
 * Recursively processes children to apply semantic highlighting to text nodes
 */
function processChildren(children: ReactNode): ReactNode {
  if (typeof children === 'string') {
    const processed = processTextWithHighlighting(children);
    return processed.length === 1 ? (
      processed[0]
    ) : (
      <Fragment>{processed}</Fragment>
    );
  }

  if (Array.isArray(children)) {
    return children.map((child, index) => {
      if (typeof child === 'string') {
        const processed = processTextWithHighlighting(child);
        return <Fragment key={index}>{processed}</Fragment>;
      }
      return child;
    });
  }

  return children;
}

/**
 * Renders markdown content with proper styling for MILES chat responses.
 * Includes semantic highlighting for financial data (currency, percentages, trends).
 * Supports GitHub Flavored Markdown including tables, bold, lists, code blocks, etc.
 */
function MilesMarkdown({ content }: MilesMarkdownProps) {
  const components: Components = {
    // Paragraphs - with semantic highlighting
    p: ({ children }) => (
      <p className="mb-3 leading-relaxed last:mb-0">
        {processChildren(children)}
      </p>
    ),

    // Bold text - enhanced styling
    strong: ({ children }) => (
      <strong className="font-bold text-gray-900">
        {processChildren(children)}
      </strong>
    ),

    // Italic text
    em: ({ children }) => (
      <em className="italic text-gray-600">{processChildren(children)}</em>
    ),

    // Headings - more prominent styling
    h1: ({ children }) => (
      <h1 className="mb-3 mt-4 text-lg font-bold text-gray-900 first:mt-0">
        {processChildren(children)}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="mb-2 mt-4 text-base font-bold text-gray-900 first:mt-0">
        {processChildren(children)}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="mb-2 mt-3 text-sm font-bold text-gray-900 first:mt-0">
        {processChildren(children)}
      </h3>
    ),

    // Unordered lists - enhanced with better spacing and styling
    ul: ({ children }) => <ul className="my-3 space-y-2">{children}</ul>,

    // Ordered lists
    ol: ({ children }) => (
      <ol className="my-3 ml-4 list-decimal space-y-2">{children}</ol>
    ),

    // List items - enhanced with icon-style bullets
    li: ({ children }) => (
      <li className="flex items-start gap-2 text-gray-700">
        <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary/60" />
        <span className="flex-1">{processChildren(children)}</span>
      </li>
    ),

    // Code blocks
    code: ({ className, children }) => {
      const isInline = !className;
      if (isInline) {
        return (
          <code className="rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-xs font-medium text-gray-800">
            {children}
          </code>
        );
      }
      return (
        <code className="block overflow-x-auto rounded-lg bg-gray-800 p-3 font-mono text-xs text-gray-100">
          {children}
        </code>
      );
    },

    // Preformatted text (code blocks)
    pre: ({ children }) => (
      <pre className="my-3 overflow-x-auto rounded-lg bg-gray-800 p-3">
        {children}
      </pre>
    ),

    // Blockquotes - styled as callout boxes
    blockquote: ({ children }) => (
      <blockquote className="my-3 flex gap-3 rounded-lg border-l-4 border-amber-400 bg-amber-50 p-3">
        <Info size={20} className="mt-0.5 flex-shrink-0 text-amber-600" />
        <div className="flex-1 text-amber-800">{processChildren(children)}</div>
      </blockquote>
    ),

    // Links
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary"
      >
        {processChildren(children)}
      </a>
    ),

    // Horizontal rule - enhanced separator
    hr: () => <hr className="my-4 border-0 border-t-2 border-gray-100" />,

    // Tables - enhanced styling
    table: ({ children }) => (
      <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          {children}
        </table>
      </div>
    ),

    thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,

    tbody: ({ children }) => (
      <tbody className="divide-y divide-gray-100 bg-white">{children}</tbody>
    ),

    tr: ({ children }) => <tr className="hover:bg-gray-50">{children}</tr>,

    th: ({ children }) => (
      <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
        {processChildren(children)}
      </th>
    ),

    td: ({ children }) => (
      <td className="whitespace-nowrap px-4 py-3 text-gray-700">
        {processChildren(children)}
      </td>
    ),
  };

  return (
    <div className="miles-markdown text-gray-600">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}

export default MilesMarkdown;
